import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { coachQueryOptions } from "~/lib/query-options";
import { getCategoryPageContent } from "~/lib/functions/coach-group-seo";
import {
  getCategoryBySlug,
  getSubcategoriesByCategorySlug,
} from "~/lib/functions/expertise";
import { Heading } from "~/components/ui/heading";
import { CoachCard } from "~/components/coach-card";
import { Subheading } from "~/components/ui/subheading";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BlogGrid } from "~/components/blog-grid";
import { MarkdownContent } from "~/components/markdown-content";
import { FaqList } from "~/components/faq-list";
import {
  createBrandMeta,
  createServiceStructuredData,
} from "~/lib/shared/meta";

const getCategoryCoachesData = async (
  categorySlug: string,
  queryClient: QueryClient,
) => {
  const [coaches, category, page] = await Promise.all([
    queryClient.ensureQueryData(coachQueryOptions.byCategory(categorySlug)),
    getCategoryBySlug({ data: { slug: categorySlug } }),
    getCategoryPageContent({ data: { categorySlug } }),
  ]);
  const subcategories = await getSubcategoriesByCategorySlug({
    data: { categorySlug },
  });
  return { coaches, category, subcategories, categorySlug, page };
};

export const Route = createFileRoute(
  "/_public/coaches/category/$categorySlug/",
)({
  loader: async ({ context, params }) => {
    const data = await getCategoryCoachesData(
      params.categorySlug,
      context.queryClient,
    );
    await context.queryClient.ensureQueryData({
      queryKey: ["categoryCoaches", params.categorySlug],
      queryFn: () => data,
    });
    return data;
  },
  head: ({ params, loaderData }) => {
    const { category, page } = loaderData || {};
    const name = category?.listingName || category?.name || params.categorySlug;
    const categorySlug = params.categorySlug;

    const title = page?.seoTitle ?? `${name} Coaches | Dream Coach Match`;
    const description =
      page?.metaDescription ??
      `Find expert ${name} coaches on Dream Coach Match. Whether you're seeking personal growth, skill development, or specific ${name} expertise, connect with the right coach to guide you toward success.`;

    const metaConfig = createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: `Discover certified ${name} coaches on our platform. Whether you're seeking personal growth, skill development, or specific expertise in ${name}, find the right coach to guide you toward success.`,
      ogImage: `https://dreamcoachmatch.com/img/og/category-${categorySlug}.jpg`,
      ogType: "website",
      ogUrl: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      twitterCard: "summary_large_image",
      twitterTitle: `Find Expert ${name} Coaches | Dream Coach Match`,
      twitterDescription: `Connect with certified ${name} coaches who can help you achieve your goals. Professional coaching services specializing in ${name} development.`,
      twitterImage: `https://dreamcoachmatch.com/img/og/category-${categorySlug}.jpg`,
      canonical: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      keywords: [
        name,
        "coach",
        "coaching",
        "professional development",
        "personal growth",
        categorySlug,
        `${name} coaching`,
        `find ${name} coach`,
        `${name} specialist`,
        "certified coach",
        "expert coach",
      ],
      author: "Dream Coach Match",
    });

    const structuredData = createServiceStructuredData({
      name: `${name} Coaching Services`,
      description: `Expert ${name} coaches to help you achieve your goals. Professional coaching services specializing in ${name}.`,
      provider: "Dream Coach Match",
      url: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      image: `https://dreamcoachmatch.com/img/og/category-${categorySlug}.jpg`,
    });

    const collectionPageStructuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${name} Coaches`,
      description: description,
      url: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
      mainEntity: {
        "@type": "ItemList",
        name: `${name} Coaches Directory`,
        description: `Professional coaches specializing in ${name}`,
        itemListOrder: "Unordered",
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://dreamcoachmatch.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Coaches",
            item: "https://dreamcoachmatch.com/coaches",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: name,
            item: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
          },
        ],
      },
    };

    const professionalServiceStructuredData = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: `${name} Coaching`,
      description: `Professional ${name} coaching services to help you achieve your personal and professional goals.`,
      serviceType: `${name} Coaching`,
      provider: {
        "@type": "Organization",
        name: "Dream Coach Match",
        url: "https://dreamcoachmatch.com",
      },
      areaServed: "Worldwide",
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: `https://dreamcoachmatch.com/coaches/category/${categorySlug}`,
        serviceType: "Online Platform",
      },
    };

    return {
      ...metaConfig,
      scripts: [
        {
          key: "jsonld-service",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(structuredData),
          },
        },
        {
          key: "jsonld-collectionpage",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(
              collectionPageStructuredData,
            ),
          },
        },
        {
          key: "jsonld-professionalservice",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify(
              professionalServiceStructuredData,
            ),
          },
        },
      ],
    };
  },
  component: CategoryCoachesPage,
});

function CategoryCoachesPage() {
  const { categorySlug } = Route.useParams();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["categoryCoaches", categorySlug],
    queryFn: () => getCategoryCoachesData(categorySlug, queryClient),
  });

  const { coaches, category, subcategories, page } = data || {};
  const name = category?.listingName || category?.name || "";

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <Heading
        preHeadline="Dream Coach Match"
        headline={
          name ? `Looking for a ${name} Coach?` : "Looking for a Coach?"
        }
        subheadline={
          name
            ? `Discover expert ${name} coaches. Whether you're seeking personal growth, skill development, or specific ${name} expertise, find the right coach to guide you.`
            : "Discover expert coaches. Whether you're seeking personal growth, skill development, or specific expertise, find the right coach to guide you."
        }
        isH1
      />
      <BreakdownIntro
        breakdownName={name}
        breakdownTypeSlug={categorySlug}
        ctaButtonTextTemplate="Find Your Dream {} Coach"
        utmCampaignPrefix="category_coaches"
      />
      {subcategories && subcategories.length > 0 && (
        <div className="my-12">
          <Subheading className="text-center">
            Explore {name} Subcategories
          </Subheading>
          <div className="flex flex-wrap justify-center gap-4">
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to="/coaches/subcategory/$subcategorySlug"
                params={{ subcategorySlug: sub.slug }}
                className="bg-brand-100 text-brand-700 hover:bg-brand-200 shadow-brand-800/15 border-brand-300 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-150 ease-in-out hover:shadow-md"
              >
                {sub.name}
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {coaches && coaches.length > 0 ? (
          coaches.map((coach, i) => (
            <React.Fragment key={coach.id}>
              <CoachCard
                coachData={{
                  coach: {
                    id: coach.id,
                    username: coach.username,
                    fullName: coach.fullName,
                    membershipPlan: coach.membershipPlan,
                    profilePictureUrl: coach.profilePictureUrl,
                    isFeaturedCoach: coach.isFeaturedCoach,
                    isFoundingCoach: coach.isFoundingCoach,
                  },
                  profile: {
                    coachingTitle: coach.coachingTitle,
                    brandLogoUrl: coach.brandLogoUrl,
                    brandName: coach.brandName,
                    title: coach.title,
                  },
                }}
              />
              {i === 11 && (
                <MidListCTA
                  breakdownName={name}
                  breakdownTypeSlug={categorySlug}
                  preLinkText="Not sure which coach to choose?"
                  ctaLinkTextTemplate="Let us match you with the perfect {} coach!"
                  utmCampaignPrefix="category_coaches_mid"
                />
              )}
            </React.Fragment>
          ))
        ) : (
          <p className="col-span-full text-center text-lg text-gray-500">
            No coaches found in this category yet.
          </p>
        )}
      </div>
      <BottomCTA
        itemCount={coaches?.length || 0}
        itemTypePlural="coaches"
        breakdownName={name}
        breakdownTypeSlug={categorySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate="Ready to find your dream {} coach?"
        mainCtaButtonTextTemplate="Try our free {} Matchmaker tool"
        utmCampaignPrefix="category_coaches_bottom"
      />
      <div className="mx-auto max-w-3xl">
        <BlogGrid
          limit={2}
          className="mt-8"
          query={{ "data.tags": { $elemMatch: { $eq: categorySlug } } }}
          heading="Useful Related Articles"
        />
      </div>
      {page?.contentMd && (
        <div className="prose mx-auto mt-12">
          <MarkdownContent content={page.contentMd} />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
